import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment } from 'react';
export function Markdown({ text, emphasizeTrailingQuestion = false, forcePrompt = false, }) {
    const blocks = parseBlocks(text.split('\n'));
    const promptIndex = emphasizeTrailingQuestion ? findTrailingQuestion(blocks) : -1;
    return (_jsx(_Fragment, { children: blocks.map((block, index) => (_jsx(Fragment, { children: renderBlock(block, forcePrompt || index === promptIndex) }, index))) }));
}
const HEADING = /^(#{1,6})\s+(.*)$/;
const RULE = /^ {0,3}([-*_])(?:\s*\1){2,}\s*$/;
const QUOTE = /^ {0,3}>\s?(.*)$/;
const UL_ITEM = /^(\s*)[-*+]\s+(.*)$/;
const OL_ITEM = /^(\s*)(\d+)[.)]\s+(.*)$/;
const FENCE = /^\s*```(.*)$/;
const TABLE_ROW = /^\s*\|(.+)\|\s*$/;
const TABLE_DIVIDER = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
/* ------------------------------------------------------------------ parse */
function parseBlocks(lines) {
    const blocks = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        if (!line.trim()) {
            i++;
            continue;
        }
        const fence = FENCE.exec(line);
        if (fence) {
            const body = [];
            i++;
            while (i < lines.length && !FENCE.test(lines[i]))
                body.push(lines[i++]);
            i++; // closing fence (or end of input)
            blocks.push({ kind: 'code', text: body.join('\n') });
            continue;
        }
        if (RULE.test(line)) {
            blocks.push({ kind: 'rule' });
            i++;
            continue;
        }
        const heading = HEADING.exec(line);
        if (heading) {
            blocks.push({ kind: 'heading', level: heading[1].length, text: heading[2] });
            i++;
            continue;
        }
        if (QUOTE.test(line)) {
            const body = [];
            while (i < lines.length && QUOTE.test(lines[i])) {
                body.push(QUOTE.exec(lines[i])[1]);
                i++;
            }
            blocks.push({ kind: 'quote', blocks: parseBlocks(body) });
            continue;
        }
        // A GFM table needs its divider row on the next line to qualify.
        if (TABLE_ROW.test(line) && lines[i + 1] && TABLE_DIVIDER.test(lines[i + 1])) {
            const columns = splitRow(line);
            // `---:` right-aligns a column (numbers); left and centre stay the default.
            const align = splitRow(lines[i + 1]).map((cell) => (/^-{2,}:$/.test(cell.trim()) ? 'right' : undefined));
            const rows = [];
            i += 2;
            while (i < lines.length && TABLE_ROW.test(lines[i]))
                rows.push(splitRow(lines[i++]));
            blocks.push({ kind: 'table', columns, rows, align });
            continue;
        }
        if (UL_ITEM.test(line) || OL_ITEM.test(line)) {
            const [list, next] = parseList(lines, i);
            blocks.push(list);
            i = next;
            continue;
        }
        // Paragraph: run to the next blank line or block-level construct.
        const body = [];
        while (i < lines.length && lines[i].trim() && !startsBlock(lines[i])) {
            body.push(lines[i++]);
        }
        // A paragraph line immediately followed by a list is the list's lead-in,
        // which is why lists must be detected before the paragraph run ends.
        if (body.length)
            blocks.push({ kind: 'paragraph', lines: body });
        else
            i++; // safety: never stall
    }
    return blocks;
}
function splitRow(line) {
    return (TABLE_ROW.exec(line)?.[1] ?? '').split('|').map((cell) => cell.trim());
}
function startsBlock(line) {
    return (HEADING.test(line) ||
        RULE.test(line) ||
        QUOTE.test(line) ||
        UL_ITEM.test(line) ||
        OL_ITEM.test(line) ||
        FENCE.test(line));
}
/** Consumes one list, grouping deeper-indented lines into nested items. */
function parseList(lines, start) {
    const first = UL_ITEM.exec(lines[start]) ?? OL_ITEM.exec(lines[start]);
    const baseIndent = first[1].length;
    const ordered = OL_ITEM.test(lines[start]);
    const startNumber = ordered ? Number(OL_ITEM.exec(lines[start])[2]) : 1;
    const items = [];
    let i = start;
    while (i < lines.length) {
        const line = lines[i];
        if (!line.trim()) {
            // A blank line ends the list unless the next line continues it.
            const next = lines[i + 1];
            if (!next?.trim() || indentOf(next) < baseIndent)
                break;
            i++;
            continue;
        }
        const item = UL_ITEM.exec(line) ?? OL_ITEM.exec(line);
        const indent = indentOf(line);
        if (item && indent === baseIndent) {
            // Same list level, and the marker type must match to stay one list.
            if (OL_ITEM.test(line) !== ordered)
                break;
            items.push([item[item.length - 1]]);
            i++;
            continue;
        }
        if (indent > baseIndent && items.length) {
            // Belongs to the current item — dedent and let recursion handle it.
            items[items.length - 1].push(line.slice(baseIndent + 1));
            i++;
            continue;
        }
        break;
    }
    return [
        { kind: 'list', ordered, start: startNumber, items: items.map((lines) => parseBlocks(lines)) },
        i,
    ];
}
function indentOf(line) {
    return line.length - line.trimStart().length;
}
/**
 * Is this text a short follow-up question worth lifting out of the prose?
 *
 * Exported because the caller often knows more than this component does: when
 * the backend splits a reply into content blocks, each block renders as its
 * own `Markdown`, so only the caller can tell whether anything preceded it.
 */
export function isTrailingQuestion(text) {
    const trimmed = text.trim();
    return trimmed.endsWith('?') && trimmed.length <= 220;
}
/**
 * Index of a trailing question paragraph within a single rendered string.
 * Requires something before it — a message that is only a question needs no
 * distinction from itself.
 */
function findTrailingQuestion(blocks) {
    if (blocks.length < 2)
        return -1;
    const index = blocks.length - 1;
    const block = blocks[index];
    if (block.kind !== 'paragraph')
        return -1;
    return isTrailingQuestion(block.lines.join(' ')) ? index : -1;
}
/* ----------------------------------------------------------------- render */
function renderBlock(block, isPrompt) {
    switch (block.kind) {
        case 'heading': {
            const Tag = `h${Math.min(block.level + 2, 6)}`;
            return _jsx(Tag, { className: "ac-md-heading", children: renderInline(block.text) });
        }
        case 'rule':
            return _jsx("hr", { className: "ac-md-rule" });
        case 'table':
            return (_jsx("div", { className: "ac-table-scroll", role: "region", "aria-label": "Table", tabIndex: 0, children: _jsxs("table", { className: "ac-table", children: [!!block.columns.length && (_jsx("thead", { children: _jsx("tr", { children: block.columns.map((column, i) => (_jsx("th", { scope: "col", className: block.align[i] === 'right' ? 'ac-num' : undefined, children: renderInline(column) }, i))) }) })), _jsx("tbody", { children: block.rows.map((row, i) => (_jsx("tr", { children: row.map((cell, j) => (_jsx("td", { className: block.align[j] === 'right' ? 'ac-num' : undefined, children: renderInline(cell) }, j))) }, i))) })] }) }));
        case 'code':
            return (_jsx("pre", { children: _jsx("code", { children: block.text }) }));
        case 'quote':
            return (_jsx("blockquote", { className: "ac-md-quote", children: block.blocks.map((child, i) => (_jsx(Fragment, { children: renderBlock(child, false) }, i))) }));
        case 'list': {
            const children = block.items.map((item, i) => (_jsx("li", { children: renderItem(item) }, i)));
            return block.ordered ? (_jsx("ol", { className: "ac-md-list", start: block.start, children: children })) : (_jsx("ul", { className: "ac-md-list", children: children }));
        }
        case 'paragraph':
            return (_jsx("p", { className: isPrompt ? 'ac-md-prompt' : undefined, children: block.lines.map((line, i) => (_jsxs(Fragment, { children: [i > 0 && _jsx("br", {}), renderInline(line.replace(/\s+$/, ''))] }, i))) }));
    }
}
/**
 * List items render "tight" — a leading paragraph becomes bare inline content
 * rather than a nested `<p>`, so `- Billing` is `<li>Billing</li>`. Any
 * following blocks (a nested list, a second paragraph) render normally.
 */
function renderItem(blocks) {
    return blocks.map((child, i) => {
        if (i === 0 && child.kind === 'paragraph') {
            return (_jsx(Fragment, { children: child.lines.map((line, j) => (_jsxs(Fragment, { children: [j > 0 && _jsx("br", {}), renderInline(line.replace(/\s+$/, ''))] }, j))) }, i));
        }
        return _jsx(Fragment, { children: renderBlock(child, false) }, i);
    });
}
/* ----------------------------------------------------------------- inline */
const INLINE_RE = new RegExp([
    '\\*\\*\\*(?=\\S)([\\s\\S]*?\\S)\\*\\*\\*', // 1 bold italic
    '\\*\\*(?=\\S)([\\s\\S]*?\\S)\\*\\*', //       2 bold
    '~~(?=\\S)([\\s\\S]*?\\S)~~', //               3 strikethrough
    '`([^`]+)`', //                                4 inline code
    '\\[([^\\]]+)\\]\\(([^)\\s]+)\\)', //          5,6 link
    '\\*(?=\\S)([^*\\n]*?\\S)\\*', //              7 italic
    '_(?=\\S)([^_\\n]*?\\S)_', //                  8 italic (underscore)
    'https?://[^\\s<>()\\[\\]]+', //               9 bare URL
].join('|'), 'g');
function renderInline(text) {
    const out = [];
    let cursor = 0;
    let key = 0;
    for (const match of text.matchAll(INLINE_RE)) {
        const index = match.index ?? 0;
        const token = match[0];
        // `_` is skipped mid-word so snake_case identifiers survive intact.
        if (token.startsWith('_') && /\w/.test(text[index - 1] ?? ''))
            continue;
        if (index > cursor)
            out.push(text.slice(cursor, index));
        if (match[1] !== undefined) {
            out.push(_jsx("strong", { children: _jsx("em", { children: match[1] }) }, key++));
        }
        else if (match[2] !== undefined) {
            out.push(_jsx("strong", { children: match[2] }, key++));
        }
        else if (match[3] !== undefined) {
            out.push(_jsx("del", { children: match[3] }, key++));
        }
        else if (match[4] !== undefined) {
            out.push(_jsx("code", { children: match[4] }, key++));
        }
        else if (match[5] !== undefined) {
            out.push(_jsx("a", { href: safeHref(match[6]), target: "_blank", rel: "noopener noreferrer", children: match[5] }, key++));
        }
        else if (match[7] !== undefined || match[8] !== undefined) {
            out.push(_jsx("em", { children: match[7] ?? match[8] }, key++));
        }
        else {
            out.push(_jsx("a", { href: safeHref(token), target: "_blank", rel: "noopener noreferrer", children: token }, key++));
        }
        cursor = index + token.length;
    }
    if (cursor < text.length)
        out.push(text.slice(cursor));
    return out;
}
/** Blocks `javascript:` and other script-bearing schemes in model output. */
function safeHref(url) {
    const trimmed = url.trim();
    return /^(https?:|mailto:|tel:|\/|#)/i.test(trimmed) ? trimmed : '#';
}
